/**
 * The drawer standard (AIC-503, Bill 2026-10-04): one sidebar for everything a page looks something up in.
 *
 * - A true sidebar: fixed below the chrome still on screen, flush right, square, full screen at 720px and under. It always
 *   overlays: the page behind it never moves, is inert and does not scroll. One scrim (--ipd-scrim) closes it, as Close,
 *   Esc and the browser's Back do. Two widths: 440px, and `wide` 720px.
 * - Header: the heading (h2, which takes focus on open) with an optional sub line, optional previous / next (‹ ›) and Close, all
 *   44px; an optional tabs row under it. An optional footer is pinned under the scrolling body.
 * - DrawerFrame is the markup and the heading focus; screens/useDrawers.tsx (the Assignment page's six drawers, with their
 *   one-at-a-time, opener and unsaved-edits rules) builds on it. Drawer is the same frame with the whole contract built in
 *   (scroll lock, inert page, Tab kept inside, Esc, Back, focus returned) for the other surfaces: the Results evidence
 *   sheet, the Assignments preview, Settings' change history.
 *
 * Lightboxes (./Lightbox.tsx) are for a short decision or a form; a drawer is for looking at something beside the page.
 * A lightbox opened from a drawer sits above it (z-index) and takes Esc while it is open.
 */
import { useEffect, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { DRAWER_ENTRY, pushEntry, replaceEntry } from "../router.tsx";
import { inertAround, keepTabInside } from "./modalLayer.ts";
import { canReturnTo } from "./Panel.tsx";
import { lockScroll } from "./scrollLock.ts";
import { Icon } from "./Sprites.tsx";
import "./Drawer.css";

/** Previous / next in the header (‹ ›). */
interface DrawerNav { readonly label: string; readonly onClick: () => void; readonly disabled?: boolean }

interface DrawerFrameProps {
  /** The panel's id: a #hash focus target (tabindex -1). */
  readonly id: string;
  readonly headingId: string;
  /** The dialog's accessible name when it isn't its heading (the Assignments preview: "Assignment preview"). */
  readonly label?: string;
  readonly title: ReactNode;
  readonly sub?: ReactNode;
  /** The Close control's accessible name ("Close the interview spec"). */
  readonly closeLabel: string;
  readonly onClose: () => void;
  /** 720px instead of 440px. */
  readonly wide?: boolean;
  readonly prev?: DrawerNav;
  readonly next?: DrawerNav;
  /** A tab row under the header (role=tablist). */
  readonly tabs?: ReactNode;
  /** Pinned under the scrolling body. */
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly bodyClassName?: string;
  readonly bodyProps?: HTMLAttributes<HTMLDivElement> & Partial<Record<`data-${string}`, string>>;
  /** Goes up each time the viewer opens the drawer: its heading takes focus then. 0 leaves focus where it is. */
  readonly focusKey?: number;
  readonly panelRef?: Ref<HTMLElement>;
  /** The element a layer treats as the drawer's host (scrim, panel and whatever else must stay usable). */
  readonly hostRef?: Ref<HTMLDivElement>;
  readonly hostProps?: HTMLAttributes<HTMLDivElement>;
  readonly panelProps?: HTMLAttributes<HTMLElement>;
  /** Rendered in the host beside the panel (a confirm dialog that must stay usable while the page is inert). */
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}

export function DrawerFrame(p: DrawerFrameProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const focusKey = p.focusKey ?? 1;
  useEffect(() => {
    if (focusKey > 0) heading.current?.focus();
  }, [focusKey]);
  const step = (n: DrawerNav, glyph: string) => (
    <button type="button" className="ipd-drawer-step" aria-label={n.label} disabled={n.disabled} onClick={n.onClick}>{glyph}</button>
  );
  return (
    <div className="ipd-drawer-host" ref={p.hostRef} {...p.hostProps}>
      {/* A scrim over the page, which closes the drawer as Close does. Not a control of its own (Close and Esc are), so it
          isn't in the tab order or the accessibility tree. */}
      <div className="ipd-drawer-scrim" aria-hidden="true" onClick={p.onClose} />
      <section
        ref={p.panelRef} id={p.id} tabIndex={-1} role="dialog" aria-modal="true" aria-label={p.label} aria-labelledby={p.label ? undefined : p.headingId}
        className={`ipd-drawer${p.wide ? " ipd-drawer--wide" : ""}${p.className ? ` ${p.className}` : ""}`} {...p.panelProps}
      >
        <header className="ipd-drawer-head">
          <div className="ipd-drawer-top">
            <div className="ipd-drawer-title">
              <h2 id={p.headingId} tabIndex={-1} ref={heading}>{p.title}</h2>
              {p.sub ? <p className="ipd-drawer-sub">{p.sub}</p> : null}
            </div>
            <div className="ipd-drawer-acts">
              {p.prev ? step(p.prev, "‹") : null}
              {p.next ? step(p.next, "›") : null}
              <button type="button" className="ipd-panel-x ipd-drawer-x" aria-label={p.closeLabel} onClick={p.onClose}><Icon name="x" className="ipd-i ipd-panel-x-i" /></button>
            </div>
          </div>
          {p.tabs}
        </header>
        <div {...p.bodyProps} className={`ipd-drawer-body${p.bodyClassName ? ` ${p.bodyClassName}` : ""}`}>{p.children}</div>
        {p.footer ? <footer className="ipd-drawer-foot">{p.footer}</footer> : null}
      </section>
      {p.aside}
    </div>
  );
}

/** The drawer's heading, for the engines that move focus back to it. */
export const headingOf = (panel: HTMLElement | null): HTMLElement | null => panel?.querySelector<HTMLElement>(".ipd-drawer-title h2") ?? null;

/** Where Esc first leaves the field: text entry, not a checkbox, radio or button-type input. */
const FIELD = "input:not([type='checkbox'], [type='radio'], [type='button'], [type='submit'], [type='reset'], [type='image']), textarea, select, [contenteditable]:not([contenteditable='false'])";

/**
 * Esc for an open drawer. In a field inside it, Esc first leaves the field (keeping what was typed, which closing would
 * lose) for the drawer's heading; the next Esc closes. Not for a key a dialog opened from the drawer has.
 */
export function drawerEscape(e: { key: string; defaultPrevented: boolean; target: EventTarget | null; preventDefault(): void }, panel: HTMLElement | null, close: () => void): void {
  if (e.key !== "Escape" || e.defaultPrevented) return;
  const t = e.target as HTMLElement;
  if (t.closest("dialog")) return;
  e.preventDefault();
  if (panel?.contains(t) && t.closest(FIELD)) {
    t.blur();
    headingOf(panel)?.focus({ preventScroll: true });
    return;
  }
  close();
}

/**
 * Browser Back closes an open drawer (AIC-503): opening adds a history entry of the same URL, and a Back that pops it
 * calls `onBack`, which closes the drawer, or returns false to keep it open (it asks about unsaved edits first; the entry
 * is then put back, so staying keeps Back working). Closing any other way takes the entry away again. A Back that leaves the
 * page is the router's, not ours.
 */
/** The key an open drawer's history entry carries in its state. */
export { DRAWER_ENTRY };
const MARK = DRAWER_ENTRY;
let marks = 0;
/**
 * The layers (a drawer, a lightbox over it) that hold a history entry, bottom first. A layer that closes marks its record closed
 * and, a tick later, every closed layer on top of the stack is unwound together with one history.go(-n): two layers closing in
 * one commit (Approve closing a lightbox and the sheet) leave no dead entry, and one that opens in the place of another (a
 * lightbox handing over to another) takes the closed one's entry over in place instead of popping it.
 */
interface Layer { mark: number; closed: boolean }
const layers: Layer[] = [];
let unwinding = false;
function unwind(): void {
  unwinding = false;
  const closed: Layer[] = [];
  while (layers.length && layers[layers.length - 1]!.closed) closed.push(layers.pop()!);
  const st = window.history.state as Record<string, unknown> | null;
  // They hold their entries only while the topmost is the entry we are on (a navigation away has moved on, or a Back popped it).
  if (closed.length && st?.[MARK] === closed[0]!.mark) window.history.go(-closed.length);
}
export function useBackCloses(active: boolean, onBack: () => boolean | void): void {
  const back = useRef(onBack);
  back.current = onBack;
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const mine = ++marks;
    const where = window.location.pathname;
    const at = (state: unknown) => !!state && typeof state === "object" && (state as Record<string, unknown>)[MARK] === mine;
    // After this commit, so a development double-mount (StrictMode) adds one entry, not two.
    let record: Layer | null = null;
    let gone = false;
    queueMicrotask(() => {
      if (gone) return;
      const top = layers[layers.length - 1];
      const st = window.history.state as Record<string, unknown> | null;
      if (top?.closed && st?.[MARK] === top.mark) {
        replaceEntry({ [MARK]: mine });
        top.mark = mine;
        top.closed = false;
        record = top;
      } else {
        pushEntry({ [MARK]: mine });
        record = { mark: mine, closed: false };
        layers.push(record);
      }
    });
    const onPop = () => {
      if (!record || at(window.history.state) || window.location.pathname !== where) return;
      if (back.current() === false) pushEntry({ [MARK]: mine });
    };
    window.addEventListener("popstate", onPop);
    return () => {
      gone = true;
      window.removeEventListener("popstate", onPop);
      // Closed any other way: the entry goes, with those of the layers closing beside it. Two microtasks on, so a layer opening in
      // this commit (its own effect's microtask) has taken the entry over by then.
      if (record && !unwinding) {
        unwinding = true;
        queueMicrotask(() => queueMicrotask(unwind));
      }
      if (record) record.closed = true;
    };
  }, [active]);
}

/** The page behind a drawer does not scroll and is inert (the Lightbox's shared, ref-counted lock). */
function useModalLayer(active: boolean, host: HTMLElement | null, live = ""): void {
  useEffect(() => {
    if (!active) return;
    return lockScroll();
  }, [active]);
  useLayoutEffect(() => {
    if (!active || !host) return;
    return inertAround(host, live);
  }, [active, host, live]);
}

interface DrawerProps extends Omit<DrawerFrameProps, "hostRef" | "panelRef" | "hostProps" | "panelProps"> {
  /**
   * Browser Back closes it (default). Off when the URL already says it is open (the Assignments preview's ?assignment=) and
   * Back already closes it.
   */
  readonly history?: boolean;
  /**
   * Focus goes back to the control that opened it when it closes (default), else to what `returnFocus` finds. Off when the
   * caller restores focus itself.
   */
  readonly restoreFocus?: boolean;
  /**
   * Page controls that stay usable beside the drawer (a selector): not inert, and Tab goes on to them after the drawer's last
   * control and back. The Results sheet keeps the call player bar (".cp-dock") this way.
   */
  readonly alsoLive?: string;
  readonly returnFocus?: () => HTMLElement | null | undefined;
  readonly panelRef?: Ref<HTMLElement>;
}

/** The whole contract, for a drawer that is simply open or not (render it while open). */
export function Drawer({ history = true, restoreFocus = true, returnFocus, panelRef, alsoLive = "", ...p }: DrawerProps) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const panel = useRef<HTMLElement | null>(null);
  const setPanel = (el: HTMLElement | null) => {
    panel.current = el;
    if (typeof panelRef === "function") panelRef(el);
    else if (panelRef) (panelRef as { current: HTMLElement | null }).current = el;
  };
  const closeRef = useRef(p.onClose);
  closeRef.current = p.onClose;
  const fallback = useRef(returnFocus);
  fallback.current = returnFocus;
  const restore = useRef(restoreFocus);
  restore.current = restoreFocus;

  // The opener, before anything inside takes focus; focus goes back to it when the drawer goes.
  useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (!restore.current) return;
      const back = canReturnTo(opener) ? opener : null;
      setTimeout(() => (back ?? fallback.current?.() ?? null)?.focus(), 0);
    };
  }, []);

  useModalLayer(true, host, alsoLive);
  useBackCloses(history, () => closeRef.current());
  // Opened without moving focus (a step with the keyboard), the page it was on is inert now: the heading takes focus.
  useLayoutEffect(() => {
    if (host && !host.contains(document.activeElement)) headingOf(panel.current)?.focus({ preventScroll: true });
  }, [host]);

  // Tab and Esc, last: after the drawer's own controls and the ones portalled into it (the Results transcript) have had
  // the key. A key from a dialog or a lightbox opened over the drawer is theirs.
  useEffect(() => {
    if (!host) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const t = e.target instanceof HTMLElement ? e.target : null;
      const loose = t === document.body || t === document.documentElement;
      const also = alsoLive ? [...document.querySelectorAll<HTMLElement>(alsoLive)] : [];
      const inside = !!t && (host.contains(t) || also.some((x) => x.contains(t)));
      if (e.key === "Tab") {
        if (inside) keepTabInside(e, panel.current, also);
        return;
      }
      // Esc on a control kept usable beside the drawer (the call player bar) closes the drawer too.
      if (e.key !== "Escape" || (!loose && !inside)) return;
      if (loose && document.querySelector(".ipd-lb, dialog[open]")) return;
      drawerEscape(e, panel.current, () => closeRef.current());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [host, alsoLive]);

  return <DrawerFrame {...p} hostRef={setHost} panelRef={setPanel} />;
}
